
import {
  InspectionResultName,
  MilestoneStatusName,
  useClientReportDetailSectionQuery
} from 'generated/graphql';

import { DateTime } from 'luxon';
import React from 'react';

export interface IData {
  id: string;
  description: string;
  deliveries: {
    id: string;
    clientNotes: string;
    updatedAt: DateTime;
    dateReceived: DateTime | null;
    title: string;
    status: string;
    isIncluded: boolean;
    quantity: number;
    imageUrls: string[];
  }[];
  attachments: {
    id: string;
    fileName: string;
    url: string;
    formattedFileSize: string;
  }[];
  photos: {
    id: string;
    url: string;
    included: boolean;
  }[];
  links: {
    id: string;
    notesForClient: string;
    url: string;
  }[];
  inspections: {
    id: string;
    inspectorName: string;
    systemInspected: string;
    notesForClient: string;
    inspectionStatus: InspectionResultName;
    updated: DateTime;
    isIncluded: boolean;
  }[];
  milestones: {
    id: string;
    updatedAt: DateTime;
    name: string;
    notesForClient: string;
    statusName: MilestoneStatusName;
    statusDisplayName: string;
    included: boolean;
  }[];
}

export function useQuery(id: string) {
  const tuple = useClientReportDetailSectionQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const clientReport = tuple.data?.clientReport;
      const data: IData = {
        id: clientReport?.id ?? '',
        description: clientReport?.description ?? '',
        deliveries: clientReport?.deliveries.map((node) => ({
          id: node.id,
          clientNotes: node.notesForClient,
          dateReceived: node.receivedDate ?? null,
          title: node.deliveryName,
          updatedAt: DateTime.now(),
          isIncluded: node.included ?? false,
          status: node.receivedDate ? 'delivered' : 'inProgress',
          quantity: 1,
          imageUrls: node.attachments.map((node) => node.url)
        })) ?? [],
        attachments: clientReport?.attachments.map((node) => ({
          id: node.id,
          fileName: node.attachment?.fileName ?? '',
          url: node.attachment?.url ?? '',
          formattedFileSize: node.attachment?.formattedFileSize ?? ''
        })) ?? [],
        photos: clientReport?.photos.map((node) => ({
          id: node.id ?? '',
          url: node.attachment?.url ?? '',
          included: node.included ?? false

        })) ?? [],
        links: clientReport?.links.map((node) => ({
          id: node.id ?? '',
          notesForClient: node.notesForClient,
          url: node.url

        })) ?? [],
        inspections: clientReport?.inspections.map((node) => ({
          id: node.id ?? '',
          inspectorName: node.inspector.fullName ?? '',
          systemInspected: node.inspectionName ?? '',
          notesForClient: node.notesForClient ?? '',
          inspectionStatus: node.inspectionResult.name ?? 'approved',
          updated: node.createdAt ?? '',
          isIncluded: node.included ?? false
        })) ?? [],
        milestones: clientReport?.milestones.map((node) => ({
          id: node.id ?? '',
          updatedAt: node.milestoneUpdated ?? '',
          name: node.milestoneName ?? '',
          notesForClient: node.notesForClient ?? '',
          statusName: node.milestoneStatus.name ?? '',
          statusDisplayName: node.milestoneStatus.displayName ?? '',
          included: node.included ?? false
        })) ?? []
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
